class EditorialGallery extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.masonryInstances = new Map();
    this.resizeObserver = null;
    this.revealObserver = null;
    this.handlePrevious = () => this.navigate(-1);
    this.handleNext = () => this.navigate(1);
    this.handleScroll = () => this.updateArrowState();
    this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.handleMotionPreferenceChange = (event) => {
      if (event.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-gallery-slide]"));
    this.viewport = this.querySelector("[data-gallery-viewport]");
    this.previousButton = this.querySelector("[data-gallery-previous]");
    this.nextButton = this.querySelector("[data-gallery-next]");
    this.navigation = this.querySelector("[data-gallery-navigation]");
    this.currentCounter = this.querySelector("[data-gallery-current]");
    this.totalCounter = this.querySelector("[data-gallery-total]");

    this.slides.forEach((slide, index) => {
      if (index !== 0) {
        slide.hidden = true;
      }
    });

    this.setupReveal();
    this.setupNavigation();
    this.setupEditorEvents();
    this.observeResize();
    this.reducedMotionQuery.addEventListener("change", this.handleMotionPreferenceChange);
    this.startAutoplay();

    if (this.isMasonry) {
      this.initializeMasonryWhenReady();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    this.revealObserver?.disconnect();
    this.viewport?.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.reducedMotionQuery.removeEventListener("change", this.handleMotionPreferenceChange);
    this.masonryInstances.forEach((instance) => instance.destroy());
    this.masonryInstances.clear();
  }

  get isHorizontalScroll() {
    return this.dataset.horizontalScroll === "true";
  }

  get isMasonry() {
    // "masonryLayout" statt "masonry": data-masonry würde die Auto-Init der
    // Masonry-Bibliothek auf diesem Element auslösen (deklarative HTML-API).
    return this.dataset.masonryLayout === "true" && !this.isHorizontalScroll;
  }

  get isSlider() {
    return this.dataset.slider === "true" && !this.isHorizontalScroll;
  }

  initializeMasonryWhenReady(attempt = 0) {
    if (typeof window.Masonry !== "function") {
      if (attempt < 100) {
        window.setTimeout(() => this.initializeMasonryWhenReady(attempt + 1), 50);
      }
      return;
    }

    this.layoutSlide(this.currentIndex);
  }

  layoutSlide(index) {
    if (!this.isMasonry || typeof window.Masonry !== "function") {
      return;
    }

    const slide = this.slides[index];
    const grid = slide?.querySelector(".editorial-gallery__grid");

    if (!grid) {
      return;
    }

    if (!this.masonryInstances.has(grid)) {
      const masonry = new window.Masonry(grid, {
        itemSelector: ".editorial-gallery__item",
        columnWidth: ".editorial-gallery__sizer",
        percentPosition: true,
        transitionDuration: 0
      });

      this.masonryInstances.set(grid, masonry);
    } else {
      this.masonryInstances.get(grid).layout();
    }
  }

  setupReveal() {
    const revealElements = this.querySelectorAll('[reveal-on-scroll="true"]');

    // Ohne Bewegungspräferenz greift die Theme-Regel `opacity: 0` nicht –
    // ebenso ohne IntersectionObserver-Support: dann sofort einblenden.
    const motionSafe = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

    if (!motionSafe || !("IntersectionObserver" in window)) {
      revealElements.forEach((element) => element.removeAttribute("reveal-on-scroll"));
      return;
    }

    // Der Observer prüft die Sichtbarkeit in beiden Achsen – Kacheln, die im
    // Scroll-Modus rechts außerhalb liegen, blenden erst beim Hineinscrollen ein.
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          this.revealElement(entry.target);
          this.revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -100px 0px" }
    );

    revealElements.forEach((element) => this.revealObserver.observe(element));
  }

  revealElement(element) {
    // Attribut entfernen, damit die CSS-Regel `opacity: 0` nicht mehr greift;
    // Startwert per Inline-Style halten und dann sauber einfaden.
    element.style.opacity = "0";
    element.removeAttribute("reveal-on-scroll");

    if (typeof element.animate !== "function") {
      element.style.opacity = "";
      return;
    }

    const animation = element.animate({ opacity: [0, 1] }, { duration: 400, easing: "ease" });
    const clearInlineOpacity = () => {
      element.style.opacity = "";
    };

    animation.finished.then(clearInlineOpacity).catch(clearInlineOpacity);
  }

  navigate(direction) {
    if (this.isHorizontalScroll) {
      this.scrollByPage(direction);
    } else {
      this.showSlide(this.currentIndex + direction);
    }
  }

  scrollByPage(direction) {
    if (!this.viewport) {
      return;
    }

    const motionSafe = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

    this.viewport.scrollBy({
      left: direction * this.viewport.clientWidth * 0.8,
      behavior: motionSafe ? "smooth" : "auto"
    });
  }

  setupNavigation() {
    if (this.totalCounter) {
      this.totalCounter.textContent = this.slides.length;
    }

    if (this.isHorizontalScroll) {
      this.viewport?.addEventListener("scroll", this.handleScroll, { passive: true });
      this.previousButton?.addEventListener("click", this.handlePrevious);
      this.nextButton?.addEventListener("click", this.handleNext);
      this.updateScrollNavigation();
      return;
    }

    if (this.navigation && this.isSlider && this.slides.length > 1) {
      this.navigation.hidden = false;
      this.previousButton?.addEventListener("click", this.handlePrevious);
      this.nextButton?.addEventListener("click", this.handleNext);
    }
  }

  updateScrollNavigation() {
    if (!this.navigation || !this.viewport) {
      return;
    }

    // Pfeile nur zeigen, wenn es tatsächlich überstehenden Inhalt gibt.
    const scrollable = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
    this.navigation.hidden = !scrollable;
    this.updateArrowState();
  }

  updateArrowState() {
    if (!this.viewport || !this.isHorizontalScroll) {
      return;
    }

    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    this.previousButton?.toggleAttribute("disabled", this.viewport.scrollLeft <= 1);
    this.nextButton?.toggleAttribute("disabled", this.viewport.scrollLeft >= maxScroll - 1);
  }

  showSlide(nextIndex, { restartAutoplay = true } = {}) {
    if (!this.isSlider || this.slides.length < 2) {
      return;
    }

    const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[normalizedIndex];

    currentSlide.classList.remove("is-selected");
    currentSlide.hidden = true;
    currentSlide.setAttribute("aria-hidden", "true");

    nextSlide.hidden = false;
    nextSlide.setAttribute("aria-hidden", "false");
    nextSlide.classList.add("is-selected");

    this.currentIndex = normalizedIndex;
    this.layoutSlide(this.currentIndex);

    if (this.currentCounter) {
      this.currentCounter.textContent = this.currentIndex + 1;
    }

    if (restartAutoplay) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    if (
      !this.isSlider ||
      this.dataset.autoplay !== "true" ||
      this.slides.length < 2 ||
      document.hidden ||
      this.reducedMotionQuery.matches
    ) {
      return;
    }

    const delay = Number.parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.autoplayTimer = window.setInterval(() => {
      this.showSlide(this.currentIndex + 1, { restartAutoplay: false });
    }, delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  setupEditorEvents() {
    if (!window.Shopify?.designMode) {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      return;
    }

    this.addEventListener("shopify:block:select", (event) => {
      const selectedItem = event.target.closest(".editorial-gallery__item");

      if (this.isHorizontalScroll) {
        selectedItem?.scrollIntoView({ behavior: this.reducedMotionQuery.matches ? "auto" : "smooth", inline: "center", block: "nearest" });
        return;
      }

      if (!this.isSlider) {
        this.layoutSlide(this.currentIndex);
        return;
      }

      const selectedGroup = Number.parseInt(selectedItem?.dataset.galleryGroup, 10);
      const slideIndex = this.slides.findIndex((slide) =>
        slide.querySelector(`[data-gallery-group="${selectedGroup}"]`)
      );

      if (slideIndex >= 0 && slideIndex !== this.currentIndex) {
        this.showSlide(slideIndex);
      } else {
        this.layoutSlide(this.currentIndex);
      }
    });
  }

  observeResize() {
    if (!("ResizeObserver" in window)) {
      return;
    }

    if (this.isHorizontalScroll && this.viewport) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollNavigation();
      });
      this.resizeObserver.observe(this.viewport);
      return;
    }

    if (this.isMasonry) {
      this.resizeObserver = new ResizeObserver(() => {
        this.layoutSlide(this.currentIndex);
      });
      this.resizeObserver.observe(this);
    }
  }
}

if (!customElements.get("editorial-gallery")) {
  customElements.define("editorial-gallery", EditorialGallery);
}
